import type { FormulaDefinition } from "./types";

// Helper to reduce boilerplate
function a(key: string): { type: "assumption"; key: string } {
  return { type: "assumption", key };
}
function c(key: string): { type: "context"; key: string } {
  return { type: "context", key };
}
function s(stepId: string): { type: "step"; stepId: string } {
  return { type: "step", stepId };
}
function k(value: number): { type: "constant"; value: number } {
  return { type: "constant", value };
}

// ============================================================================
// AUTONOMOUS CODING
// ============================================================================

const coder_labor_savings: FormulaDefinition = {
  familyId: "coder_labor_savings",
  familyName: "Coder Labor Savings",
  finalStepId: "annualSavings",
  steps: [
    {
      stepId: "autonomousEncounters",
      label: "Autonomous Encounters",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("total_encounters"), label: "Total Encounters" },
        {
          source: a("autonomous_coding_rate"),
          label: "Autonomous Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "assistedEncounters",
      label: "Assisted Encounters",
      op: "subtract",
      unit: "count",
      inputs: [
        { source: c("total_encounters"), label: "Total Encounters" },
        { source: s("autonomousEncounters"), label: "Autonomous Encounters" },
      ],
    },
    {
      stepId: "autonomousMinutes",
      label: "Autonomous Minutes Saved",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: s("autonomousEncounters"), label: "Autonomous Encounters" },
        {
          source: c("weighted_avg_coding_time_minutes"),
          label: "Avg Coding Minutes",
        },
      ],
    },
    {
      stepId: "autonomousHoursSaved",
      label: "Autonomous Hours Saved",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("autonomousMinutes"), label: "Autonomous Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "assistedMinutes",
      label: "Assisted Minutes Saved",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: s("assistedEncounters"), label: "Assisted Encounters" },
        {
          source: a("time_reduction_assisted_pct"),
          label: "Time Reduction %",
          unit: "%",
        },
        {
          source: c("weighted_avg_coding_time_minutes"),
          label: "Avg Coding Minutes",
        },
      ],
    },
    {
      stepId: "assistedHoursSaved",
      label: "Assisted Hours Saved",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("assistedMinutes"), label: "Assisted Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "grossHoursSaved",
      label: "Gross Hours Saved",
      op: "add",
      unit: "hours",
      inputs: [
        { source: s("autonomousHoursSaved"), label: "Autonomous Hours" },
        { source: s("assistedHoursSaved"), label: "Assisted Hours" },
      ],
    },
    {
      stepId: "qaMinutes",
      label: "QA Review Minutes",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: s("autonomousEncounters"), label: "Autonomous Encounters" },
        { source: c("qa_review_rate"), label: "QA Review Rate", unit: "%" },
        { source: c("qa_minutes_per_review"), label: "QA Min/Review" },
      ],
    },
    {
      stepId: "qaHours",
      label: "QA Review Hours",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("qaMinutes"), label: "QA Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "netHoursSaved",
      label: "Net Hours Saved",
      op: "subtract",
      unit: "hours",
      inputs: [
        { source: s("grossHoursSaved"), label: "Gross Hours" },
        { source: s("qaHours"), label: "QA Hours" },
      ],
    },
    {
      stepId: "rawSavings",
      label: "Raw Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("netHoursSaved"), label: "Net Hours Saved" },
        {
          source: c("hourly_coder_cost"),
          label: "Hourly Coder Cost",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualSavings",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const coding_denial_reduction: FormulaDefinition = {
  familyId: "coding_denial_reduction",
  familyName: "Coding Denial Reduction",
  finalStepId: "annualSavings",
  steps: [
    {
      stepId: "currentDenials",
      label: "Current Coding Denials",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("total_encounters"), label: "Total Encounters" },
        {
          source: c("coding_denial_rate"),
          label: "Coding Denial Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "denialsPrevented",
      label: "Denials Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("currentDenials"), label: "Current Denials" },
        {
          source: a("denial_reduction_pct"),
          label: "Denial Reduction %",
          unit: "%",
        },
      ],
    },
    {
      stepId: "writeoffRate",
      label: "Write-off Rate (1 - Recovery)",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: c("denial_recovery_rate"), label: "Recovery Rate" },
      ],
    },
    {
      stepId: "netPerDenial",
      label: "Net Cost per Denial",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("avg_denial_value"), label: "Avg Denial Value", unit: "$" },
        { source: s("writeoffRate"), label: "Write-off Rate" },
      ],
    },
    {
      stepId: "annualSavings",
      label: "Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("denialsPrevented"), label: "Denials Prevented" },
        { source: s("netPerDenial"), label: "Net Cost per Denial" },
      ],
    },
  ],
};

const vendor_cost: FormulaDefinition = {
  familyId: "vendor_cost",
  familyName: "Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("total_encounters"), label: "Total Encounters" },
        {
          source: a("vendor_per_encounter_fee"),
          label: "Per-Encounter Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        {
          source: a("vendor_annual_license"),
          label: "Annual License",
          unit: "$",
        },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const implementation_cost: FormulaDefinition = {
  familyId: "implementation_cost",
  familyName: "Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("implementation_cost"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// AMBIENT CLINICAL DOCUMENTATION
// ============================================================================

const ambient_retention_value: FormulaDefinition = {
  familyId: "ambient_retention_value",
  familyName: "Physician Retention Value",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "burnoutPPx10",
      label: "Burnout PP (scaled)",
      op: "multiply",
      unit: "rate",
      inputs: [
        {
          source: a("ambient_burnout_reduction_pp"),
          label: "Burnout Reduction PP",
        },
        { source: k(10), label: "10 (PP→coefficient)" },
      ],
    },
    {
      stepId: "turnoverReduction",
      label: "Turnover Reduction",
      op: "multiply",
      unit: "pct",
      inputs: [
        { source: s("burnoutPPx10"), label: "Scaled Burnout PP" },
        {
          source: c("turnover_reduction_per_burnout_pp"),
          label: "Turnover per Burnout PP",
        },
      ],
    },
    {
      stepId: "physRetained",
      label: "Physicians Retained",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("physician_ftes"), label: "Physician FTEs" },
        { source: s("turnoverReduction"), label: "Turnover Reduction" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Retention Value",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("physRetained"), label: "Physicians Retained" },
        {
          source: a("ambient_replacement_cost"),
          label: "Replacement Cost",
          unit: "$",
        },
      ],
    },
  ],
};

const ambient_productivity: FormulaDefinition = {
  familyId: "ambient_productivity",
  familyName: "Productivity / wRVU Uplift",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "annualWRVUs",
      label: "Annual Additional wRVUs",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("effective_providers"), label: "Effective Providers" },
        { source: a("ambient_wrvu_uplift_per_month"), label: "wRVU Uplift/Mo" },
        { source: k(12), label: "12 months" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual wRVU Value",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("annualWRVUs"), label: "Annual wRVUs" },
        { source: c("dollar_per_wrvu"), label: "$/wRVU", unit: "$" },
      ],
    },
  ],
};

const ambient_time_value: FormulaDefinition = {
  familyId: "ambient_time_value",
  familyName: "Physician Time Savings",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "totalMinutes",
      label: "Total Minutes Saved",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: c("effective_providers"), label: "Effective Providers" },
        { source: a("ambient_minutes_saved_per_day"), label: "Min Saved/Day" },
        { source: c("working_days_per_year"), label: "Working Days/Year" },
      ],
    },
    {
      stepId: "totalHrs",
      label: "Total Hours Saved",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("totalMinutes"), label: "Total Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "valueHrs",
      label: "Recoverable Hours",
      op: "multiply",
      unit: "hours",
      inputs: [
        { source: s("totalHrs"), label: "Total Hours" },
        {
          source: a("ambient_time_value_fraction"),
          label: "Recoverable Fraction",
          unit: "%",
        },
      ],
    },
    {
      stepId: "rawSavings",
      label: "Raw Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("valueHrs"), label: "Recoverable Hours" },
        {
          source: c("hourly_physician_cost"),
          label: "Hourly Physician Cost",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const ambient_vendor_cost: FormulaDefinition = {
  familyId: "ambient_vendor_cost",
  familyName: "Ambient Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("effective_providers"), label: "Effective Providers" },
        { source: a("ambient_vendor_pppm"), label: "PPPM Cost", unit: "$" },
        { source: k(12), label: "12 months" },
      ],
    },
  ],
};

const ambient_implementation_cost: FormulaDefinition = {
  familyId: "ambient_implementation_cost",
  familyName: "Ambient Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("ambient_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("ambient_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// PRIOR AUTHORIZATION AUTOMATION
// ============================================================================

const pa_labor_savings: FormulaDefinition = {
  familyId: "pa_labor_savings",
  familyName: "PA Labor Savings",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "touchlessPAs",
      label: "Touchless PAs",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("estimated_pa_volume"), label: "PA Volume" },
        { source: a("pa_touchless_rate"), label: "Touchless Rate", unit: "%" },
      ],
    },
    {
      stepId: "assistedPAs",
      label: "Assisted PAs",
      op: "subtract",
      unit: "count",
      inputs: [
        { source: c("estimated_pa_volume"), label: "PA Volume" },
        { source: s("touchlessPAs"), label: "Touchless PAs" },
      ],
    },
    {
      stepId: "touchlessMinutes",
      label: "Touchless Minutes Saved",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: s("touchlessPAs"), label: "Touchless PAs" },
        { source: c("avg_handling_minutes"), label: "Avg Handling Minutes" },
      ],
    },
    {
      stepId: "touchlessHrs",
      label: "Touchless Hours Saved",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("touchlessMinutes"), label: "Touchless Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "assistedMinutes",
      label: "Assisted Minutes Saved",
      op: "multiply",
      unit: "minutes",
      inputs: [
        { source: s("assistedPAs"), label: "Assisted PAs" },
        {
          source: a("pa_assisted_time_reduction"),
          label: "Time Reduction %",
          unit: "%",
        },
        { source: c("avg_handling_minutes"), label: "Avg Handling Minutes" },
      ],
    },
    {
      stepId: "assistedHrs",
      label: "Assisted Hours Saved",
      op: "divide",
      unit: "hours",
      inputs: [
        { source: s("assistedMinutes"), label: "Assisted Minutes" },
        { source: k(60), label: "60" },
      ],
    },
    {
      stepId: "totalHrs",
      label: "Total Hours Saved",
      op: "add",
      unit: "hours",
      inputs: [
        { source: s("touchlessHrs"), label: "Touchless Hours" },
        { source: s("assistedHrs"), label: "Assisted Hours" },
      ],
    },
    {
      stepId: "rawSavings",
      label: "Raw Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("totalHrs"), label: "Total Hours" },
        {
          source: c("hourly_pa_staff_cost"),
          label: "Hourly PA Staff Cost",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const pa_denial_reduction: FormulaDefinition = {
  familyId: "pa_denial_reduction",
  familyName: "PA Denial Reduction",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "curDenials",
      label: "Current Denials",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("estimated_pa_volume"), label: "PA Volume" },
        { source: c("weighted_denial_rate"), label: "Denial Rate", unit: "%" },
      ],
    },
    {
      stepId: "prevented",
      label: "Denials Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("curDenials"), label: "Current Denials" },
        {
          source: a("pa_denial_reduction_pct"),
          label: "Denial Reduction %",
          unit: "%",
        },
      ],
    },
    {
      stepId: "reworkSavings",
      label: "Rework Cost Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        { source: c("weighted_rework_cost"), label: "Rework Cost", unit: "$" },
      ],
    },
    {
      stepId: "writeoffRate",
      label: "Write-off Rate (1 - Recovery)",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: c("baseline_recovery_rate"), label: "Recovery Rate" },
      ],
    },
    {
      stepId: "curWriteoffs",
      label: "Current Write-offs",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("curDenials"), label: "Current Denials" },
        { source: s("writeoffRate"), label: "Write-off Rate" },
      ],
    },
    {
      stepId: "woPrevent",
      label: "Write-offs Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("curWriteoffs"), label: "Current Write-offs" },
        {
          source: a("pa_writeoff_reduction_pct"),
          label: "Write-off Reduction %",
          unit: "%",
        },
      ],
    },
    {
      stepId: "writeoffRevenue",
      label: "Write-off Revenue Recovered",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("woPrevent"), label: "Write-offs Prevented" },
        { source: c("avg_denial_value"), label: "Avg Denial Value", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Total Annual Value",
      op: "add",
      unit: "currency",
      inputs: [
        { source: s("reworkSavings"), label: "Rework Savings" },
        { source: s("writeoffRevenue"), label: "Write-off Recovery" },
      ],
    },
  ],
};

const pa_abandonment_recovery: FormulaDefinition = {
  familyId: "pa_abandonment_recovery",
  familyName: "PA Abandonment Recovery",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "atRisk",
      label: "At-Risk PAs",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("estimated_pa_volume"), label: "PA Volume" },
        {
          source: c("pa_abandonment_at_risk_fraction"),
          label: "At-Risk Fraction",
          unit: "%",
        },
      ],
    },
    {
      stepId: "retained",
      label: "PAs Retained",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("atRisk"), label: "At-Risk PAs" },
        {
          source: a("pa_abandonment_prevention"),
          label: "Prevention Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "grossRevenue",
      label: "Gross Revenue Recovered",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("retained"), label: "PAs Retained" },
        {
          source: c("avg_pa_encounter_value"),
          label: "Avg PA Encounter Value",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Net Revenue (Contribution Margin)",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("grossRevenue"), label: "Gross Revenue" },
        {
          source: c("weighted_contribution_margin"),
          label: "Contribution Margin",
          unit: "%",
        },
      ],
    },
  ],
};

const pa_vendor_cost: FormulaDefinition = {
  familyId: "pa_vendor_cost",
  familyName: "PA Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("estimated_pa_volume"), label: "PA Volume" },
        {
          source: a("pa_per_transaction"),
          label: "Per-Transaction Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("pa_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const pa_implementation_cost: FormulaDefinition = {
  familyId: "pa_implementation_cost",
  familyName: "PA Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("pa_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("pa_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// SCHEDULING OPTIMIZATION
// ============================================================================

const sched_noshow_recovery: FormulaDefinition = {
  familyId: "sched_noshow_recovery",
  familyName: "No-Show Recovery",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "currentNoshows",
      label: "Current No-Shows",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("annual_appointments"), label: "Annual Appointments" },
        { source: c("current_noshow_rate"), label: "No-Show Rate", unit: "%" },
      ],
    },
    {
      stepId: "prevented",
      label: "No-Shows Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("currentNoshows"), label: "Current No-Shows" },
        {
          source: a("sched_noshow_reduction"),
          label: "Reduction Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "converted",
      label: "Converted to Visits",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("prevented"), label: "Prevented No-Shows" },
        {
          source: a("sched_noshow_conversion"),
          label: "Conversion Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Revenue Recovered",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("converted"), label: "Converted Visits" },
        {
          source: c("avg_appointment_value"),
          label: "Avg Appointment Value",
          unit: "$",
        },
      ],
    },
  ],
};

const sched_backfill_revenue: FormulaDefinition = {
  familyId: "sched_backfill_revenue",
  familyName: "Cancellation Backfill Revenue",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "cancellations",
      label: "Annual Cancellations",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("annual_appointments"), label: "Annual Appointments" },
        {
          source: c("cancellation_rate"),
          label: "Cancellation Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "additionalBackfills",
      label: "Additional Backfills",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("cancellations"), label: "Cancellations" },
        {
          source: a("sched_backfill_improvement"),
          label: "Backfill Improvement",
          unit: "%",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Backfill Revenue",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("additionalBackfills"), label: "Additional Backfills" },
        {
          source: c("avg_appointment_value"),
          label: "Avg Appointment Value",
          unit: "$",
        },
      ],
    },
  ],
};

const sched_staff_efficiency: FormulaDefinition = {
  familyId: "sched_staff_efficiency",
  familyName: "Scheduling Staff Efficiency",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("sched_staff_ftes"), label: "Scheduling FTEs" },
        {
          source: a("sched_staff_time_savings"),
          label: "Time Savings %",
          unit: "%",
        },
        { source: c("sched_staff_cost"), label: "Staff Cost", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const sched_vendor_cost: FormulaDefinition = {
  familyId: "sched_vendor_cost",
  familyName: "Scheduling Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("annual_appointments"), label: "Annual Appointments" },
        {
          source: a("sched_per_appointment"),
          label: "Per-Appointment Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        {
          source: a("sched_annual_license"),
          label: "Annual License",
          unit: "$",
        },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const sched_implementation_cost: FormulaDefinition = {
  familyId: "sched_implementation_cost",
  familyName: "Scheduling Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("sched_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("sched_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// DENIAL MANAGEMENT
// ============================================================================

const denial_prevention: FormulaDefinition = {
  familyId: "denial_prevention",
  familyName: "Denial Prevention",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "prevented",
      label: "Denials Prevented (Marginal)",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("scoped_denials"), label: "Scoped Denials" },
        {
          source: a("denial_prevention_rate"),
          label: "Prevention Rate",
          unit: "%",
        },
        {
          source: c("marginal_prevention_factor"),
          label: "Marginal Factor",
          unit: "%",
        },
      ],
    },
    {
      stepId: "reworkSaved",
      label: "Rework Cost Saved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        {
          source: c("rework_cost_per_denial"),
          label: "Rework Cost/Denial",
          unit: "$",
        },
      ],
    },
    {
      stepId: "writeoffRate",
      label: "Write-off Rate",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: c("baseline_recovery_rate"), label: "Recovery Rate" },
      ],
    },
    {
      stepId: "revenueSaved",
      label: "Revenue Preserved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        { source: c("avg_claim_value"), label: "Avg Claim Value", unit: "$" },
        { source: c("denial_revenue_index"), label: "Denial Rev Index" },
        { source: s("writeoffRate"), label: "Write-off Rate" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Total Annual Value",
      op: "add",
      unit: "currency",
      inputs: [
        { source: s("reworkSaved"), label: "Rework Saved" },
        { source: s("revenueSaved"), label: "Revenue Preserved" },
      ],
    },
  ],
};

const denial_recovery: FormulaDefinition = {
  familyId: "denial_recovery",
  familyName: "Recovery Rate Improvement",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "remainingRate",
      label: "Remaining Rate (1 - Prevention)",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: a("denial_prevention_rate"), label: "Prevention Rate" },
      ],
    },
    {
      stepId: "remainingDenials",
      label: "Remaining Denials",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("scoped_denials"), label: "Scoped Denials" },
        { source: s("remainingRate"), label: "Remaining Rate" },
      ],
    },
    {
      stepId: "additionalRecov",
      label: "Additional Recovered",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("remainingDenials"), label: "Remaining Denials" },
        {
          source: a("denial_recovery_improvement"),
          label: "Recovery Improvement",
          unit: "%",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Recovery Value",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("additionalRecov"), label: "Additional Recovered" },
        { source: c("avg_claim_value"), label: "Avg Claim Value", unit: "$" },
        { source: c("denial_revenue_index"), label: "Denial Rev Index" },
      ],
    },
  ],
};

const denial_staff_efficiency: FormulaDefinition = {
  familyId: "denial_staff_efficiency",
  familyName: "Denial Staff Efficiency",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("denial_staff_ftes"), label: "Denial Staff FTEs" },
        {
          source: a("denial_staff_time_savings"),
          label: "Time Savings %",
          unit: "%",
        },
        { source: c("denial_staff_cost"), label: "Staff Cost", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const denial_vendor_cost: FormulaDefinition = {
  familyId: "denial_vendor_cost",
  familyName: "Denial Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("total_claims_volume"), label: "Total Claims Volume" },
        { source: a("denial_per_claim"), label: "Per-Claim Fee", unit: "$" },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        {
          source: a("denial_annual_license"),
          label: "Annual License",
          unit: "$",
        },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const denial_implementation_cost: FormulaDefinition = {
  familyId: "denial_implementation_cost",
  familyName: "Denial Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("denial_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("denial_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// CALL CENTER / CONVERSATIONAL AI
// ============================================================================

const cc_deflection_savings: FormulaDefinition = {
  familyId: "cc_deflection_savings",
  familyName: "Call Deflection Savings",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "deflected",
      label: "Calls Deflected",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("call_volume"), label: "Call Volume" },
        {
          source: a("cc_deflection_rate"),
          label: "Deflection Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "fullyResolved",
      label: "Fully Resolved (no callback)",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("deflected"), label: "Calls Deflected" },
        {
          source: c("call_resolution_rate"),
          label: "Resolution Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "rawSavings",
      label: "Raw Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("fullyResolved"), label: "Fully Resolved" },
        { source: c("cost_per_call"), label: "Cost per Call", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const cc_after_hours: FormulaDefinition = {
  familyId: "cc_after_hours",
  familyName: "After-Hours Coverage",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "handled",
      label: "After-Hours Calls Handled",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("after_hours_calls"), label: "After-Hours Calls" },
        { source: a("cc_after_hours_rate"), label: "Handling Rate", unit: "%" },
      ],
    },
    {
      stepId: "afterHoursCost",
      label: "After-Hours Cost per Call",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("cost_per_call"), label: "Cost per Call", unit: "$" },
        { source: k(1.5), label: "After-Hours Premium" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Coverage Value",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("handled"), label: "Calls Handled" },
        { source: s("afterHoursCost"), label: "After-Hours Cost", unit: "$" },
      ],
    },
  ],
};

const cc_abandonment_recovery: FormulaDefinition = {
  familyId: "cc_abandonment_recovery",
  familyName: "Abandonment Recovery",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "currentAbandoned",
      label: "Currently Abandoned Calls",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("call_volume"), label: "Call Volume" },
        { source: c("abandonment_rate"), label: "Abandonment Rate", unit: "%" },
      ],
    },
    {
      stepId: "prevented",
      label: "Abandonments Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("currentAbandoned"), label: "Abandoned Calls" },
        {
          source: a("cc_abandonment_reduction"),
          label: "Reduction Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "recoveredAppts",
      label: "Recovered Appointments",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: s("prevented"), label: "Prevented Abandonments" },
        {
          source: c("scheduling_call_share"),
          label: "Scheduling Share",
          unit: "%",
        },
        { source: k(0.5), label: "Conversion Rate (50%)" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Revenue Recovered",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("recoveredAppts"), label: "Recovered Appointments" },
        {
          source: c("avg_appointment_value"),
          label: "Avg Appointment Value",
          unit: "$",
        },
      ],
    },
  ],
};

const cc_vendor_cost: FormulaDefinition = {
  familyId: "cc_vendor_cost",
  familyName: "Call Center Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("call_volume"), label: "Call Volume" },
        { source: a("cc_per_call"), label: "Per-Call Fee", unit: "$" },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("cc_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const cc_implementation_cost: FormulaDefinition = {
  familyId: "cc_implementation_cost",
  familyName: "Call Center Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("cc_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("cc_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// CDI (CLINICAL DOCUMENTATION INTEGRITY)
// ============================================================================

const cdi_cmi_improvement: FormulaDefinition = {
  familyId: "cdi_cmi_improvement",
  familyName: "CMI Improvement",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "grossValue",
      label: "Gross CMI Revenue Impact",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("inpatient_discharges"), label: "Inpatient Discharges" },
        { source: a("cdi_cmi_improvement"), label: "CMI Improvement" },
        {
          source: c("revenue_per_cmi_point"),
          label: "Revenue per CMI Point",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Durable Annual Value",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("grossValue"), label: "Gross Value" },
        { source: c("cmi_durability"), label: "CMI Durability", unit: "%" },
      ],
    },
  ],
};

const cdi_specialist_efficiency: FormulaDefinition = {
  familyId: "cdi_specialist_efficiency",
  familyName: "CDI Specialist Efficiency",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("cdi_specialist_ftes"), label: "CDI Specialist FTEs" },
        {
          source: a("cdi_specialist_efficiency"),
          label: "Efficiency Gain",
          unit: "%",
        },
        {
          source: c("cdi_specialist_cost"),
          label: "Specialist Cost",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const cdi_denial_prevention: FormulaDefinition = {
  familyId: "cdi_denial_prevention",
  familyName: "CDI Denial Prevention",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "prevented",
      label: "Denials Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("documentation_denials"), label: "Documentation Denials" },
        {
          source: a("cdi_denial_prevention_rate"),
          label: "Prevention Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "reworkSaved",
      label: "Rework Cost Saved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        {
          source: c("rework_cost_per_denial"),
          label: "Rework Cost",
          unit: "$",
        },
      ],
    },
    {
      stepId: "writeoffRate",
      label: "Write-off Rate",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: c("denial_recovery_rate"), label: "Recovery Rate" },
      ],
    },
    {
      stepId: "revenueSaved",
      label: "Revenue Preserved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        { source: c("avg_claim_value"), label: "Avg Claim Value", unit: "$" },
        { source: s("writeoffRate"), label: "Write-off Rate" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Total Annual Value",
      op: "add",
      unit: "currency",
      inputs: [
        { source: s("reworkSaved"), label: "Rework Saved" },
        { source: s("revenueSaved"), label: "Revenue Preserved" },
      ],
    },
  ],
};

const cdi_vendor_cost: FormulaDefinition = {
  familyId: "cdi_vendor_cost",
  familyName: "CDI Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("inpatient_discharges"), label: "Inpatient Discharges" },
        {
          source: a("cdi_per_discharge"),
          label: "Per-Discharge Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("cdi_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const cdi_implementation_cost: FormulaDefinition = {
  familyId: "cdi_implementation_cost",
  familyName: "CDI Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("cdi_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("cdi_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// PATIENT FINANCIAL CLEARANCE
// ============================================================================

const pfc_coverage_discovery: FormulaDefinition = {
  familyId: "pfc_coverage_discovery",
  familyName: "Coverage Discovery",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "discovered",
      label: "Encounters with Coverage Discovered",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("self_pay_encounters"), label: "Self-Pay Encounters" },
        {
          source: a("pfc_coverage_discovery_rate"),
          label: "Discovery Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Annual Revenue from Discovery",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("discovered"), label: "Discovered Coverage" },
        {
          source: c("net_value_per_discovery"),
          label: "Net Value per Discovery",
          unit: "$",
        },
      ],
    },
  ],
};

const pfc_eligibility_labor: FormulaDefinition = {
  familyId: "pfc_eligibility_labor",
  familyName: "Eligibility Staff Efficiency",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("eligibility_staff_ftes"), label: "Eligibility FTEs" },
        {
          source: a("pfc_verification_time_reduction"),
          label: "Time Reduction %",
          unit: "%",
        },
        { source: c("eligibility_staff_cost"), label: "Staff Cost", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const pfc_denial_prevention: FormulaDefinition = {
  familyId: "pfc_denial_prevention",
  familyName: "PFC Denial Prevention",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "prevented",
      label: "Denials Prevented",
      op: "multiply",
      unit: "count",
      inputs: [
        { source: c("registration_denials"), label: "Registration Denials" },
        {
          source: a("pfc_registration_denial_prevention"),
          label: "Prevention Rate",
          unit: "%",
        },
      ],
    },
    {
      stepId: "reworkSaved",
      label: "Rework Cost Saved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        { source: c("rework_cost"), label: "Rework Cost", unit: "$" },
      ],
    },
    {
      stepId: "writeoffRate",
      label: "Write-off Rate",
      op: "subtract",
      unit: "pct",
      inputs: [
        { source: k(1), label: "1" },
        { source: c("denial_recovery_rate"), label: "Recovery Rate" },
      ],
    },
    {
      stepId: "revenueSaved",
      label: "Revenue Preserved",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("prevented"), label: "Denials Prevented" },
        { source: c("avg_claim_value"), label: "Avg Claim Value", unit: "$" },
        { source: s("writeoffRate"), label: "Write-off Rate" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Total Annual Value",
      op: "add",
      unit: "currency",
      inputs: [
        { source: s("reworkSaved"), label: "Rework Saved" },
        { source: s("revenueSaved"), label: "Revenue Preserved" },
      ],
    },
  ],
};

const pfc_vendor_cost: FormulaDefinition = {
  familyId: "pfc_vendor_cost",
  familyName: "PFC Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("verified_encounters"), label: "Verified Encounters" },
        {
          source: a("pfc_per_encounter"),
          label: "Per-Encounter Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("pfc_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const pfc_implementation_cost: FormulaDefinition = {
  familyId: "pfc_implementation_cost",
  familyName: "PFC Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("pfc_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("pfc_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// CHARGE CAPTURE
// ============================================================================

const cc_missed_charge_recovery: FormulaDefinition = {
  familyId: "cc_missed_charge_recovery",
  familyName: "Missed Charge Recovery",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "annualValue",
      label: "Annual Recovered Revenue",
      op: "multiply",
      unit: "currency",
      inputs: [
        {
          source: c("total_missed_charge_revenue"),
          label: "Total Missed Charge Revenue",
          unit: "$",
        },
        {
          source: a("cc_missed_charge_improvement"),
          label: "Improvement Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const cc_compliance_correction: FormulaDefinition = {
  familyId: "cc_compliance_correction",
  familyName: "Compliance Correction",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "annualValue",
      label: "Annual Compliance Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("net_revenue"), label: "Net Revenue", unit: "$" },
        {
          source: a("cc_compliance_savings_pct"),
          label: "Compliance Savings %",
          unit: "%",
        },
      ],
    },
  ],
};

const cc_staff_efficiency: FormulaDefinition = {
  familyId: "cc_staff_efficiency",
  familyName: "Charge Capture Staff Efficiency",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        {
          source: c("revenue_integrity_ftes"),
          label: "Revenue Integrity FTEs",
        },
        {
          source: a("cc_staff_efficiency"),
          label: "Efficiency Gain",
          unit: "%",
        },
        { source: c("revenue_integrity_cost"), label: "Staff Cost", unit: "$" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const cc_vendor_cost_charge: FormulaDefinition = {
  familyId: "cc_vendor_cost_charge",
  familyName: "Charge Capture Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("total_encounters"), label: "Total Encounters" },
        {
          source: a("cc_per_encounter"),
          label: "Per-Encounter Fee",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("cc_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const cc_implementation_cost_charge: FormulaDefinition = {
  familyId: "cc_implementation_cost_charge",
  familyName: "Charge Capture Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("cc_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("cc_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// OR OPTIMIZATION
// ============================================================================

const or_utilization_revenue: FormulaDefinition = {
  familyId: "or_utilization_revenue",
  familyName: "OR Utilization Revenue",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "additionalHours",
      label: "Additional OR Hours Freed",
      op: "multiply",
      unit: "hours",
      inputs: [
        { source: c("or_count"), label: "OR Count" },
        { source: c("hours_per_or_per_day"), label: "Hours/OR/Day" },
        { source: c("working_days_per_year"), label: "Working Days/Year" },
        {
          source: a("or_utilization_improvement"),
          label: "Utilization Improvement",
          unit: "%",
        },
      ],
    },
    {
      stepId: "filledHours",
      label: "Revenue-Generating Hours",
      op: "multiply",
      unit: "hours",
      inputs: [
        { source: s("additionalHours"), label: "Additional Hours" },
        { source: c("fill_rate"), label: "Fill Rate", unit: "%" },
      ],
    },
    {
      stepId: "grossValue",
      label: "Gross OR Revenue",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("filledHours"), label: "Filled Hours" },
        {
          source: c("revenue_per_or_hour"),
          label: "Revenue per OR Hour",
          unit: "$",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Adjusted Annual OR Revenue",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("grossValue"), label: "Gross Value" },
        { source: c("surgical_revenue_index"), label: "Surgical Rev Index" },
        {
          source: c("incremental_case_discount"),
          label: "Incremental Discount",
          unit: "%",
        },
      ],
    },
  ],
};

const or_block_optimization: FormulaDefinition = {
  familyId: "or_block_optimization",
  familyName: "OR Block Optimization",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "totalBlockHours",
      label: "Total Block Hours",
      op: "multiply",
      unit: "hours",
      inputs: [
        { source: c("or_count"), label: "OR Count" },
        { source: c("hours_per_or_per_day"), label: "Hours/OR/Day" },
        { source: c("working_days_per_year"), label: "Working Days/Year" },
      ],
    },
    {
      stepId: "idleHoursRecovered",
      label: "Idle Hours Recovered",
      op: "multiply",
      unit: "hours",
      inputs: [
        { source: s("totalBlockHours"), label: "Total Block Hours" },
        {
          source: a("or_block_improvement"),
          label: "Block Improvement",
          unit: "%",
        },
      ],
    },
    {
      stepId: "hourlyStaffCost",
      label: "Hourly Staff Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: c("perioperative_cost"),
          label: "Annual Periop Cost",
          unit: "$",
        },
        { source: k(2080), label: "2080 hours/year" },
      ],
    },
    {
      stepId: "rawSavings",
      label: "Raw Staff Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("idleHoursRecovered"), label: "Idle Hours Recovered" },
        { source: k(0.3), label: "Staff Idle Factor (0.30)" },
        { source: s("hourlyStaffCost"), label: "Hourly Staff Cost" },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const or_staff_efficiency: FormulaDefinition = {
  familyId: "or_staff_efficiency",
  familyName: "OR Staff Overtime Reduction",
  finalStepId: "annualValue",
  steps: [
    {
      stepId: "rawSavings",
      label: "Raw Overtime Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        {
          source: c("overtime_annual_cost"),
          label: "Annual Overtime Cost",
          unit: "$",
        },
        {
          source: a("or_staff_overtime_reduction"),
          label: "OT Reduction %",
          unit: "%",
        },
      ],
    },
    {
      stepId: "annualValue",
      label: "Realized Annual Savings",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: s("rawSavings"), label: "Raw Savings" },
        {
          source: a("labor_realization_rate"),
          label: "Realization Rate",
          unit: "%",
        },
      ],
    },
  ],
};

const or_vendor_cost: FormulaDefinition = {
  familyId: "or_vendor_cost",
  familyName: "OR Vendor Cost",
  finalStepId: "annual",
  steps: [
    {
      stepId: "variable",
      label: "Variable Cost",
      op: "multiply",
      unit: "currency",
      inputs: [
        { source: c("or_count"), label: "OR Count" },
        { source: a("or_per_or_month"), label: "Per-OR/Month Fee", unit: "$" },
        { source: k(12), label: "12 months" },
      ],
    },
    {
      stepId: "annual",
      label: "Annual Vendor Cost",
      op: "add",
      unit: "currency",
      inputs: [
        { source: a("or_annual_license"), label: "Annual License", unit: "$" },
        { source: s("variable"), label: "Variable Cost" },
      ],
    },
  ],
};

const or_implementation_cost: FormulaDefinition = {
  familyId: "or_implementation_cost",
  familyName: "OR Implementation Cost",
  finalStepId: "amortized",
  steps: [
    {
      stepId: "amortized",
      label: "Amortized Annual Cost",
      op: "divide",
      unit: "currency",
      inputs: [
        {
          source: a("or_implementation"),
          label: "Implementation Cost",
          unit: "$",
        },
        {
          source: a("or_contract_term_years"),
          label: "Contract Term",
          unit: "years",
        },
      ],
    },
  ],
};

// ============================================================================
// EXPORT
// ============================================================================

export const FORMULA_DEFINITIONS: Record<string, FormulaDefinition> = {
  // Autonomous Coding
  coder_labor_savings,
  coding_denial_reduction,
  vendor_cost,
  implementation_cost,
  // Ambient Documentation
  ambient_retention_value,
  ambient_productivity,
  ambient_time_value,
  ambient_vendor_cost,
  ambient_implementation_cost,
  // PA Automation
  pa_labor_savings,
  pa_denial_reduction,
  pa_abandonment_recovery,
  pa_vendor_cost,
  pa_implementation_cost,
  // Scheduling
  sched_noshow_recovery,
  sched_backfill_revenue,
  sched_staff_efficiency,
  sched_vendor_cost,
  sched_implementation_cost,
  // Denial Management
  denial_prevention,
  denial_recovery,
  denial_staff_efficiency,
  denial_vendor_cost,
  denial_implementation_cost,
  // Call Center
  cc_deflection_savings,
  cc_after_hours,
  cc_abandonment_recovery,
  cc_vendor_cost,
  cc_implementation_cost,
  // CDI
  cdi_cmi_improvement,
  cdi_specialist_efficiency,
  cdi_denial_prevention,
  cdi_vendor_cost,
  cdi_implementation_cost,
  // PFC
  pfc_coverage_discovery,
  pfc_eligibility_labor,
  pfc_denial_prevention,
  pfc_vendor_cost,
  pfc_implementation_cost,
  // Charge Capture
  cc_missed_charge_recovery,
  cc_compliance_correction,
  cc_staff_efficiency,
  cc_vendor_cost_charge,
  cc_implementation_cost_charge,
  // OR Optimization
  or_utilization_revenue,
  or_block_optimization,
  or_staff_efficiency,
  or_vendor_cost,
  or_implementation_cost,
};
